import React, { useState, useEffect, useMemo } from "react"
import { Button, Typography, Paper } from "@material-ui/core"
import { RailsForm } from '@support/RailsForm'
import { Form as FinalForm } from "react-final-form"
import Field from '@ui/Fields/Field'
import { format } from "date-fns"
import { fetchEquipments, fetchAreas, buildQueryWithBrackets } from "@models/admin/reports"
import styles from '@theme/styles/forms'
import withStyles from '@material-ui/core/styles/withStyles'
import LoadingComponent from '@ui/LoadingComponent'
import { getToken } from '@services/authStore'
import { useErrorHandler } from '@contexts/ErrorHandlerProvider'

const SatelliteImageReport = ({ classes }) => {
  const [areas, setAreas] = useState([])
  const [stations, setStations] = useState([])
  const [pluviometers, setPluviometers] = useState([])
  const [organizationId, setOrganizationId] = useState('')
  const [dataLoaded, setDataLoaded] = useState(false)
  const [loading, setLoading] = useState(false)
  const handleError = useErrorHandler()

  useEffect(() => {
    const fetchData = async () => {
      if (!organizationId) {
        setAreas([])
        setStations([])
        setPluviometers([])
        setDataLoaded(false)
        return
      }

      try {
        const equipments = await fetchEquipments(organizationId)
        const listStations = equipments.filter(e => e.tags?.includes("weather_station"))
        const listPluviometers = equipments.filter(e => e.tags?.includes("pluviometer"))
        const listAreas = await fetchAreas(organizationId)

        setAreas(listAreas.map(({ id, name }) => ({ id, name })))
        setStations(listStations.map(({ id, name }) => ({ id, name })))
        setPluviometers(listPluviometers.map(({ id, name }) => ({ id, name })))
        setDataLoaded(true)
      } catch (error) {
        setDataLoaded(false)
        handleError(error)
      }
    }

    const timeoutId = setTimeout(() => {
      fetchData()
    }, 1000);

    return () => clearTimeout(timeoutId)
  }, [organizationId])

  const handleSubmit = (data) => {
    const { date, ...rest } = data
    const params = {
      ...rest,
      year: format(new Date(date), "yyyy"),
      month: format(new Date(date), "MM"),
      token: getToken(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }

    const query = buildQueryWithBrackets(params)
    const url = `https://reports.cultivointeligente.com.br/run_report/ndvi_report?${query}`

    window.open(url, '_blank')
  }

  const today = useMemo(() => new Date(), [])
  const currentMonth = useMemo(() => new Date(today.getFullYear(), today.getMonth()), [today])

  const initialValues = useMemo(() => ({
    date: currentMonth,
    organization_id: '',
    area_ids: [],
    station_id: '',
    pluviometer_ids: []
  }), [currentMonth])

  return (
    <>
      {loading ? <LoadingComponent /> : (
        <RailsForm
          component={FinalForm}
          action="create"
          url={`/admin/reports/ndvi_report`}
          handleSubmit={handleSubmit}
          initialValues={initialValues}
          render={({ handleSubmit, values }) => {
            if (values.organization_id !== organizationId) {
              setOrganizationId(values.organization_id || '')
            }

            return (
              <Paper className={classes.form__wrapper} elevation={0}>
                <form className={classes.form} onSubmit={handleSubmit}>
                  <header className={classes.form__header}>
                    <Typography component="h2" variant="h5">
                      {I18n.t("activerecord.models.v4/reports.ndvi")}
                    </Typography>
                  </header>
                  <main className={classes.form__fields}>
                    <Field
                      type="date"
                      label="Mês e Ano"
                      name="date"
                      views={['year', 'month']}
                      dateFormat="MM/yyyy"
                      required
                    />
                    <Field
                      type="text"
                      label="Organização"
                      name="organization_id"
                      required
                    />
                    {dataLoaded && (
                      <>
                        <Field
                          type="multiSelect"
                          data={areas}
                          label="Área"
                          name="area_ids"
                          required
                        />
                        <Field
                          type="select"
                          data={stations}
                          label="Estações Meteorológicas"
                          name="station_id"
                          required
                        />
                        <Field
                          type="multiSelect"
                          data={pluviometers}
                          label="Pluviômetros"
                          name="pluviometer_ids"
                          required
                        />
                      </>
                    )}
                  </main>
                  <footer className={classes.form__actions}>
                    <Button
                      className={classes.form__action}
                      color="primary"
                      type="submit"
                      variant="contained"
                    >
                      Gerar Relatório
                    </Button>
                  </footer>
                </form>
              </Paper>
            )
          }}
        />
      )}
    </>
  )
}

export default withStyles(styles)(SatelliteImageReport)
